/**
 * Completitud de una PERSONA (fuente de verdad única).
 *
 * La completitud es una propiedad de la persona y NO depende de la perspectiva desde
 * la que se la mire (armador o tripulante). Es decir: si una persona está COMPLETA como
 * armador, también lo está como tripulante y viceversa. Esto evita el caso histórico en
 * que un mismo usuario aparecía "Caducado" en la tabla de armadores e "Incompleto" en la
 * de tripulantes (ver docs/definicion-estados-usuario.md).
 *
 * Capa separada: el estado de la SOLICITUD (pendiente/aprobado/rechazado/caduco) sí puede
 * variar por perspectiva (futuras autorizaciones de operación). Lo único prohibido es que
 * una perspectiva con solicitud real (aprobado/rechazado/caduco/pendiente) caiga a
 * "incompleto" mientras la otra no.
 *
 * Criterios de documentación requeridos para estar COMPLETO:
 * - Persona jurídica (entidad armador): personería.
 * - Persona natural (armador natural o tripulante regular): fotografía + carnet de identidad.
 * - Patrón (persona natural con cargo 'patron'): fotografía + carnet de identidad + matrícula.
 */
export interface CompletitudInput {
  /** 'natural' | 'juridica'. Para tripulantes siempre es 'natural'. */
  tipoPersona: 'natural' | 'juridica'
  /** Id del documento de fotografía en el gestor (0 = no subido). Solo personas naturales. */
  idFotografia?: number
  /** Id del documento de carnet de identidad en el gestor (0 = no subido). Solo personas naturales. */
  idCarnet?: number
  /** true si la persona opera como patrón (requiere matrícula). */
  esPatron?: boolean
  /** Id del documento de matrícula en el gestor (0 = no subido). */
  idMatricula?: number
  /** Id del documento de personería en el gestor (0 = no subido). Solo jurídicos. */
  idPersoneria?: number
  /** true si el usuario/entidad ya cuenta con inscripción o AO aprobada.
   * Por Ley 19.628 de Protección de Datos Personales (PP-AO-14), el carnet de identidad
   * se purga permanentemente tras la aprobación, por lo que su ausencia no invalida la completitud. */
  yaAprobado?: boolean
}

export interface RequisitoFaltante {
  key: string
  label: string
}

/**
 * Devuelve si la persona está completa y, si no, qué documentos le faltan (para mostrarlos en la UI).
 * Es la fuente de verdad única; `esCompleto` deriva de aquí.
 */
export function requisitosUsuario(input: CompletitudInput): { completo: boolean; faltantes: RequisitoFaltante[] } {
  const faltantes: RequisitoFaltante[] = []

  // Jurídico (entidad): solo se exige personería; no tiene foto/CI/matrícula propios.
  if (input.tipoPersona === 'juridica') {
    if ((input.idPersoneria ?? 0) === 0) faltantes.push({ key: 'personeria', label: 'Personería jurídica' })
    return { completo: faltantes.length === 0, faltantes }
  }

  // Persona natural: fotografía obligatoria.
  if ((input.idFotografia ?? 0) === 0) faltantes.push({ key: 'fotografia', label: 'Fotografía' })
  // Carnet de identidad: obligatorio solo si no ha sido aprobado previamente (post-aprobación se purga por ley).
  if (!input.yaAprobado && (input.idCarnet ?? 0) === 0) faltantes.push({ key: 'ci', label: 'Carnet de identidad' })

  // Patrón: además requiere matrícula. El tripulante regular no la requiere.
  if (input.esPatron && (input.idMatricula ?? 0) === 0) faltantes.push({ key: 'matricula', label: 'Matrícula' })

  return { completo: faltantes.length === 0, faltantes }
}

export function esCompleto(input: CompletitudInput): boolean {
  return requisitosUsuario(input).completo
}
